/**
 * Admin — grille de prix des SKILLCARDS (migration 0304). Écritures directes
 * PostgREST sous RLS admin (`skillcard_pricing: admin_update`, packs /
 * programmes déjà admin). Tables postérieures aux types générés → client générique.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SkillcardPricingSettings } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export function useSkillcardPricingSettings() {
  return useQuery({
    queryKey: ['skillcard-pricing-settings'],
    queryFn: async (): Promise<SkillcardPricingSettings & { updated_at: string }> => {
      const { data, error } = await db
        .from('skillcard_pricing')
        .select(
          'unit_price_cents, pack_tiers, programme_discount_pct, programme_max_cents, round_step_cents, validity_months, free_sample_questions, updated_at',
        )
        .eq('id', true)
        .single();
      if (error) throw new Error(error.message);
      return data as SkillcardPricingSettings & { updated_at: string };
    },
  });
}

export function useSaveSkillcardPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (s: SkillcardPricingSettings) => {
      const { error } = await db
        .from('skillcard_pricing')
        .update({
          unit_price_cents: s.unit_price_cents,
          pack_tiers: s.pack_tiers,
          programme_discount_pct: s.programme_discount_pct,
          programme_max_cents: s.programme_max_cents ?? null,
          round_step_cents: s.round_step_cents,
          validity_months: s.validity_months,
          free_sample_questions: s.free_sample_questions,
        })
        .eq('id', true);
      if (error) throw new Error(error.message);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['skillcard-pricing-settings'] });
      void qc.invalidateQueries({ queryKey: ['skillcard-price-grid'] });
    },
  });
}

/** Prix forcé d'un pack / programme (centimes, null = grille automatique). */
export function useSetPriceOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { kind: 'pack' | 'programme'; id: string; cents: number | null }) => {
      const table = p.kind === 'pack' ? 'curriculum_packs' : 'curriculum_programmes';
      const { error } = await db
        .from(table)
        .update({ price_override_cents: p.cents })
        .eq('id', p.id);
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['skillcard-price-grid'] }),
  });
}

export interface SkillcardPurchaseRow {
  id: string;
  kind: 'card' | 'pack' | 'programme';
  label: string;
  amount_cents: number;
  card_count: number;
  status: 'pending' | 'paid' | 'failed';
  created_at: string;
  paid_at: string | null;
}

export function useRecentSkillcardPurchases() {
  return useQuery({
    queryKey: ['skillcard-purchases-admin'],
    queryFn: async (): Promise<SkillcardPurchaseRow[]> => {
      const { data, error } = await db
        .from('skillcard_purchases')
        .select('id, kind, label, amount_cents, card_count, status, created_at, paid_at')
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw new Error(error.message);
      return (data ?? []) as SkillcardPurchaseRow[];
    },
  });
}

/** « 1,90 » → 190 ; vide → null ; invalide → NaN. */
export function parseEurosToCents(v: string): number | null {
  const t = v.trim().replace(/\s|€/g, '').replace(',', '.');
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return Number.NaN;
  return Math.round(Number(t) * 100);
}

export function centsToEurosInput(c: number | null | undefined): string {
  return c == null ? '' : (c / 100).toFixed(2).replace('.', ',');
}
